/**
 * C-GTCC-VIEW-OVW1 — typed read client for the GTCC ops endpoints.
 *
 * The shapes below are the data contract of J-GTCC-API-OPS1, transcribed from
 * the C-instruction body without addition: the same three GETs, the same field
 * names, the same nullability. J owns the server side; until J is merged the
 * views render fixtures (`../fixtures/ops.*.json`) that carry the values of
 * `GTCC_REFERENCE_2026-09-01.html`. After J merges this module is rebased onto
 * the real DTO type instead of re-declaring it.
 *
 * Read-only by construction: `gtccGet` wraps `apiGet` only — no mutation verb,
 * no upstream URL, no PromQL.
 *
 * Nothing is derived here. Relative labels ("heute", "−41 min"), statuses and
 * verdicts arrive decided by the API; the local clock never produces a number
 * (Owner decisions P8/P9/P10).
 */
import {
  gtccGet,
  type GtccMeasured,
  type GtccResult,
  type GtccStatus,
  type GtccVerdict,
} from "./gtcc-result.js";

/* --------------------------------------------------------------- paths */

const OPS_BASE = "/v1/admin/gtcc/ops";

export const OPS_VEHICLE_FLOW_PATH = `${OPS_BASE}/vehicle-flow`;
export const OPS_BACKUP_READINESS_PATH = `${OPS_BASE}/backup-readiness`;
export const OPS_RECENT_CHANGES_PATH = `${OPS_BASE}/recent-changes`;

/* ---------------------------------------------------------------- types */

/**
 * Per-tenant telemetry. This is the only measured source for the Risk Radar
 * columns FAHRZEUGE / STILL > 24 H / ALARME 7 T / DATA CONF.; the existing
 * risk-radar module (`./risk.ts`) contributes tenant identity and its own
 * signals, never these counts.
 *
 * Every count is nullable: a tenant whose telemetry is not instrumented has no
 * number, and the table then reads "nicht messbar" instead of a zero that
 * would look like a measurement.
 */
export interface OpsTenantFlowDto {
  readonly tenantId: string;
  /** Short display name as the reference prints it, e.g. `URBAGEC`. */
  readonly name: string;
  readonly vehicles: number | null;
  readonly sendingNow: number | null;
  readonly silentOver24h: number | null;
  readonly alerts7d: number | null;
  readonly dataConfidencePercent: number | null;
}

export interface OpsVehicleFlowDto extends GtccMeasured {
  /** Platform totals of the "Fahrzeuge senden aktuell" row. */
  readonly sendingNow: number | null;
  readonly vehiclesTotal: number | null;
  readonly tenants: ReadonlyArray<OpsTenantFlowDto>;
}

/**
 * Backup & Recovery Readiness as the reference row prints it:
 * `Class 2+3 ✓ · Offsite ROT`. The class list and the offsite label are
 * decided by the API — the view neither knows the class catalogue nor what
 * makes an offsite chain red.
 */
export interface OpsBackupReadinessDto extends GtccMeasured {
  readonly classesText: string;
  readonly offsiteText: string;
  readonly verdict: GtccVerdict;
}

export type OpsChangeKind = "deploy" | "alert" | "merge" | "config" | "other";

export interface OpsChangeDto {
  readonly id: string;
  readonly kind: OpsChangeKind;
  /** Full row text, e.g. `Deploy: Rechtsseiten + Korrekturen`. */
  readonly text: string;
  /**
   * The right-hand `.mono` label: `heute`, `−41 min`, `−2 h`. Relative labels
   * come from the API because only it knows the measurement clock; deriving
   * them from `Date.now()` would print an age nobody measured.
   */
  readonly relativeText: string;
  readonly verdict: GtccVerdict;
}

export interface OpsRecentChangesDto extends GtccMeasured {
  readonly changes: ReadonlyArray<OpsChangeDto>;
}

/* -------------------------------------------------------------- fetchers */

export function fetchOpsVehicleFlow(signal?: AbortSignal): Promise<GtccResult<OpsVehicleFlowDto>> {
  return gtccGet<OpsVehicleFlowDto>(OPS_VEHICLE_FLOW_PATH, signal);
}

export function fetchOpsBackupReadiness(
  signal?: AbortSignal,
): Promise<GtccResult<OpsBackupReadinessDto>> {
  return gtccGet<OpsBackupReadinessDto>(OPS_BACKUP_READINESS_PATH, signal);
}

export function fetchOpsRecentChanges(
  signal?: AbortSignal,
): Promise<GtccResult<OpsRecentChangesDto>> {
  return gtccGet<OpsRecentChangesDto>(OPS_RECENT_CHANGES_PATH, signal);
}

/* ----------------------------------------------------------- query keys */

/*
 * Prefix convention of the GTCC query-key inventory: `["gtcc", <viewkey>, …]`.
 * The view boundary cancels and clears by exactly these prefixes, so the same
 * endpoint read from two views needs two keys — `vehicle-flow` feeds both
 * Operations Overview (`overview`) and Risk Radar (`risk`).
 *
 * Each key is an own exported array literal: A-GTCC-QUERY-KEY-CONSISTENCY1
 * reads keys statically out of the source and can follow
 * `queryKey: OPS_VEHICLE_FLOW_KEY` to this literal, while a member access
 * `OPS_QUERY_KEYS.vehicleFlow` stays unreadable for it.
 */
export const OPS_VEHICLE_FLOW_KEY = ["gtcc", "overview", "ops-vehicle-flow"] as const;
export const OPS_BACKUP_READINESS_KEY = ["gtcc", "overview", "ops-backup-readiness"] as const;
export const OPS_RECENT_CHANGES_KEY = ["gtcc", "overview", "ops-recent-changes"] as const;
export const OPS_VEHICLE_FLOW_RISK_KEY = ["gtcc", "risk", "ops-vehicle-flow"] as const;

export const OPS_QUERY_KEYS = {
  vehicleFlow: OPS_VEHICLE_FLOW_KEY,
  backupReadiness: OPS_BACKUP_READINESS_KEY,
  recentChanges: OPS_RECENT_CHANGES_KEY,
  vehicleFlowForRisk: OPS_VEHICLE_FLOW_RISK_KEY,
} as const;

/** Re-exported so consumers of this module need one import, not two. */
export type { GtccResult, GtccStatus, GtccVerdict };
